/**
 * Núcleo do login de demonstração (SPEC-v2 2.2, ADR-009): registros, sessão, auditoria e erros.
 * Compartilhado pelo `MockAuthProvider` (caminho do login, no bundle inicial) e por `team.ts`
 * (Equipe, sob demanda).
 */
import type {
  Area,
  Id,
  Ms,
  ProviderError,
  Role,
  SessionInfo,
  StudioFunction,
  SuiteAuditAction,
  SuiteAuditEntry,
  TeamMember,
} from '@rota/contracts';
import { buildSession } from './access';
import { newId, newToken, type PasswordRecord, sha256 } from './crypto';
import type { AuthStorage } from './storage';

export const SESSION_MS = 12 * 3_600_000;
export const REMEMBER_MS = 30 * 86_400_000;
export const STEP_UP_MS = 15 * 60_000;
export const INVITE_MS = 72 * 3_600_000;
export const RESET_MS = 30 * 60_000;
export const CHALLENGE_MS = 5 * 60_000;
export const PASSWORD_MIN = 15;
export const PASSWORD_MAX = 128;
/** Espera progressiva (2.2): 5 erros seguidos → 60 s, depois 5 min, depois 15 min. */
export const THROTTLE_STEPS_MS: readonly number[] = [60_000, 5 * 60_000, 15 * 60_000];
export const THROTTLE_FAILURES = 5;

export interface MemberRecord extends TeamMember {
  password: PasswordRecord | null;
  totp: { secret: string; recovery: string[]; enrolledAt: Ms } | null;
  /** Segredo gerado em "Ativar 2FA", ainda não confirmado com um código. */
  pendingTotp?: { secret: string; recovery: string[] } | null;
  /** Pessoa criada por `?demo=` (só no dev server). Não conta como dono configurado. */
  demo?: boolean;
  createdAt: Ms;
}

export interface SessionRecord {
  key: string;
  memberId: Id;
  createdAt: Ms;
  expiresAt: Ms;
  remembered: boolean;
  stepUpUntil: Ms | null;
  lastSeenAt: Ms;
  device: string;
}

export interface InviteRecord {
  key: string;
  memberId: Id;
  email: string;
  role: Role;
  functions: StudioFunction[];
  createdBy: Id;
  createdAt: Ms;
  expiresAt: Ms;
  usedAt: Ms | null;
}

export interface ResetRecord {
  key: string;
  memberId: Id;
  createdAt: Ms;
  expiresAt: Ms;
  usedAt: Ms | null;
}

export interface ThrottleRecord {
  failures: number;
  level: number;
  lockedUntil: Ms;
}

/** Onde o navegador guarda o token: `sessionStorage` (aba) e, com "Manter conectado", `localStorage`. */
export interface TokenJar {
  read(): string | null;
  write(token: string, remember: boolean, expiresAt: Ms): void;
  clear(): void;
}

export const TOKEN_KEY = 'rota:sid';

function safe(get: () => Storage | undefined): Storage | null {
  try {
    return get() ?? null;
  } catch {
    return null;
  }
}

export function browserTokenJar(): TokenJar {
  const ss = () => safe(() => globalThis.sessionStorage);
  const ls = () => safe(() => globalThis.localStorage);
  return {
    read() {
      try {
        const s = ss()?.getItem(TOKEN_KEY);
        if (s) return s;
        const raw = ls()?.getItem(TOKEN_KEY);
        if (!raw) return null;
        const v = JSON.parse(raw) as { t?: string; exp?: number };
        return typeof v.t === 'string' && typeof v.exp === 'number' && v.exp > Date.now() ? v.t : null;
      } catch {
        return null;
      }
    },
    write(token, remember, expiresAt) {
      try {
        ss()?.setItem(TOKEN_KEY, token);
        if (remember) ls()?.setItem(TOKEN_KEY, JSON.stringify({ t: token, exp: expiresAt }));
        else ls()?.removeItem(TOKEN_KEY);
      } catch {
        // storage bloqueado: a sessão vale só em memória até recarregar
      }
    },
    clear() {
      try {
        ss()?.removeItem(TOKEN_KEY);
        ls()?.removeItem(TOKEN_KEY);
      } catch {
        // nada a limpar
      }
    },
  };
}

export function memoryTokenJar(): TokenJar {
  let t: string | null = null;
  return {
    read: () => t,
    write: (token) => {
      t = token;
    },
    clear: () => {
      t = null;
    },
  };
}

export function providerError(
  code: ProviderError['code'],
  message: string,
  retryAfterMs?: number,
): ProviderError {
  return retryAfterMs == null ? { code, message } : { code, message, retryAfterMs };
}

export interface AuthContext {
  storage: AuthStorage;
  jar: TokenJar;
  iterations: number;
  now(): Ms;
  /** Origem dos links de convite e de redefinição. */
  origin: string;
  device: string;
}

export const normEmail = (e: string): string => e.trim().toLocaleLowerCase('pt-BR');

export function toTeamMember(m: MemberRecord): TeamMember {
  return {
    id: m.id,
    name: m.name,
    initials: m.initials,
    email: m.email,
    role: m.role,
    functions: [...m.functions],
    status: m.status,
    lastSeenAt: m.lastSeenAt,
    mfaEnrolled: m.mfaEnrolled,
  };
}

export async function findByEmail(ctx: AuthContext, email: string): Promise<MemberRecord | undefined> {
  const e = normEmail(email);
  const all = await ctx.storage.all<MemberRecord>('members');
  return all.find((m) => normEmail(m.email) === e && !m.demo);
}

export async function hasConfiguredOwner(ctx: AuthContext): Promise<boolean> {
  const all = await ctx.storage.all<MemberRecord>('members');
  return all.some((m) => m.role === 'owner' && !m.demo && m.password != null && m.status === 'active');
}

export function sessionInfo(m: MemberRecord, s: SessionRecord): SessionInfo {
  return buildSession(m, { expiresAt: s.expiresAt, stepUpUntil: s.stepUpUntil, remembered: s.remembered });
}

/** Abre uma sessão nova, grava o token no navegador e devolve a `SessionInfo`. */
export async function openSession(
  ctx: AuthContext,
  m: MemberRecord,
  remember: boolean,
): Promise<SessionInfo> {
  const token = newToken();
  const now = ctx.now();
  const rec: SessionRecord = {
    key: await sha256(token),
    memberId: m.id,
    createdAt: now,
    expiresAt: now + (remember ? REMEMBER_MS : SESSION_MS),
    remembered: remember,
    // Acabou de digitar a senha: vale como confirmação recente (step-up) pelos próximos 15 min.
    stepUpUntil: now + STEP_UP_MS,
    lastSeenAt: now,
    device: ctx.device,
  };
  await ctx.storage.put('sessions', rec.key, rec);
  await ctx.storage.put('members', m.id, { ...m, lastSeenAt: now });
  ctx.jar.write(token, remember, rec.expiresAt);
  return sessionInfo({ ...m, lastSeenAt: now }, rec);
}

/** Sessão atual (token do navegador → registro). `null` se não houver ou se venceu. */
export async function currentSession(
  ctx: AuthContext,
): Promise<{ rec: SessionRecord; member: MemberRecord } | null> {
  const token = ctx.jar.read();
  if (!token) return null;
  const rec = await ctx.storage.get<SessionRecord>('sessions', await sha256(token));
  const now = ctx.now();
  if (!rec || rec.expiresAt <= now) {
    if (rec) await ctx.storage.delete('sessions', rec.key);
    ctx.jar.clear();
    return null;
  }
  const member = await ctx.storage.get<MemberRecord>('members', rec.memberId);
  if (!member || member.status === 'disabled') {
    ctx.jar.clear();
    return null;
  }
  return { rec, member };
}

export async function requireSession(
  ctx: AuthContext,
): Promise<{ rec: SessionRecord; member: MemberRecord }> {
  const cur = await currentSession(ctx);
  if (!cur) throw providerError('forbidden', 'Sessão encerrada. Entre de novo.');
  return cur;
}

export async function revokeMemberSessions(ctx: AuthContext, memberId: Id, keep?: string): Promise<number> {
  const all = await ctx.storage.all<SessionRecord>('sessions');
  let n = 0;
  for (const s of all) {
    if (s.memberId === memberId && s.key !== keep) {
      await ctx.storage.delete('sessions', s.key);
      n++;
    }
  }
  return n;
}

/** Auditoria só de inserção, com cadeia de hash (10.2). */
export async function audit(
  ctx: AuthContext,
  actorId: Id | 'system',
  action: SuiteAuditAction,
  detail: string,
  area: Area | 'suite' = 'suite',
): Promise<void> {
  const head = await ctx.storage.get<{ hash: string; seq: number }>('meta', 'auditHead');
  const at = ctx.now();
  const seq = (head?.seq ?? 0) + 1;
  const base = { id: newId('aud'), at, actorId, action, area, detail };
  const prevHash = head?.hash ?? '';
  const hash = await sha256(`${prevHash}|${JSON.stringify(base)}`);
  const entry: SuiteAuditEntry & { seq: number } = { ...base, prevHash, hash, seq };
  await ctx.storage.put('audit', String(seq).padStart(9, '0'), entry);
  await ctx.storage.put('meta', 'auditHead', { hash, seq });
}

export function checkPasswordPolicy(password: string): void {
  const n = [...password].length;
  if (n < PASSWORD_MIN)
    throw providerError('invalid', `A senha precisa ter pelo menos ${PASSWORD_MIN} caracteres.`);
  if (n > PASSWORD_MAX)
    throw providerError('invalid', `A senha pode ter no máximo ${PASSWORD_MAX} caracteres.`);
}
